import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Container, Row, Col, Card, Button, Alert } from "react-bootstrap";
import PublicHeader from "../components/Layout/PublicHeader";
import Footer from "../components/Layout/Footer";
import ErrorIcon from "../assets/images/fi_alert-triangle.svg";
import "../styles/contact.css";

const validationSchema = Yup.object().shape({
  nom: Yup.string().required("Le nom est obligatoire"),
  email: Yup.string()
    .email("Email invalide")
    .required("L'email est obligatoire"),
  message: Yup.string()
    .required("Le message est obligatoire")
    .min(10, "Le message doit contenir au moins 10 caractères"),
  fichier: Yup.mixed()
    .nullable()
    .test(
      "fileSize",
      "Le fichier ne doit pas dépasser 5 Mo",
      (value) => !value || (value && value.size <= 5242880) // 5MB in bytes
    ),
});

const ContactPage = () => {
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showPhone, setShowPhone] = useState(false);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitSuccess(false);
      setSubmitError("");

      const formData = new FormData();
      formData.append("nom", values.nom);
      formData.append("email", values.email);
      formData.append("message", values.message);
      if (values.fichier) {
        formData.append("fichier", values.fichier);
      }

      // TODO: Replace with actual API endpoint
      // For now, we'll simulate sending to contact@marion-livraison.com
      const response = await fetch("/api/contact", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSubmitSuccess(true);
        resetForm();
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error("Erreur lors de l'envoi du message");
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSubmitError(
        "Une erreur est survenue lors de l'envoi de ton message. Réessaie plus tard."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <PublicHeader />

      {/* Hero Section */}
      <section className="contact-hero-section">
        <Container>
          <Row className="text-center">
            <Col>
              <h1 className="display-4 fw-bold mb-3">Contacte-nous</h1>
              <p className="lead text-muted">
                Une question ? Une demande particulière ? Notre équipe est là
                pour t'aider
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              {submitSuccess && (
                <Alert variant="success" className="mb-4">
                  <strong>Merci !</strong> Ton message a bien été envoyé. Nous
                  te répondrons dans les plus brefs délais à{" "}
                  <strong>contact@marion-livraison.com</strong>.
                </Alert>
              )}

              {submitError && (
                <Alert variant="danger" className="mb-4">
                  <strong>Erreur :</strong> {submitError}
                </Alert>
              )}

              <Card className="shadow-sm">
                <Card.Body className="p-5">
                  <h3 className="mb-4">Envoie-nous un message</h3>

                  <Formik
                    initialValues={{
                      nom: "",
                      email: "",
                      message: "",
                      fichier: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ errors, touched, setFieldValue, isSubmitting }) => (
                      <Form>
                        {/* Nom */}
                        <div className="form-floating mb-3">
                          <Field
                            type="text"
                            name="nom"
                            className={`form-control ${
                              touched.nom && errors.nom ? "is-invalid" : ""
                            }`}
                            placeholder="Nom"
                            id="floatingNom"
                          />
                          <label htmlFor="floatingNom">Nom complet *</label>
                          {touched.nom && errors.nom && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img
                                src={ErrorIcon}
                                className="me-1"
                                alt="error"
                                width="16"
                              />
                              {errors.nom}
                            </div>
                          )}
                        </div>

                        {/* Email */}
                        <div className="form-floating mb-3">
                          <Field
                            type="email"
                            name="email"
                            className={`form-control ${
                              touched.email && errors.email ? "is-invalid" : ""
                            }`}
                            placeholder="Email"
                            id="floatingEmail"
                          />
                          <label htmlFor="floatingEmail">Adresse e-mail *</label>
                          {touched.email && errors.email && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img
                                src={ErrorIcon}
                                className="me-1"
                                alt="error"
                                width="16"
                              />
                              {errors.email}
                            </div>
                          )}
                        </div>

                        {/* Message */}
                        <div className="form-floating mb-3">
                          <Field
                            as="textarea"
                            name="message"
                            className={`form-control ${
                              touched.message && errors.message
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Message"
                            id="floatingMessage"
                            style={{ height: "150px" }}
                          />
                          <label htmlFor="floatingMessage">Ton message *</label>
                          {touched.message && errors.message && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img
                                src={ErrorIcon}
                                className="me-1"
                                alt="error"
                                width="16"
                              />
                              {errors.message}
                            </div>
                          )}
                        </div>

                        {/* File Upload */}
                        <div className="mb-4">
                          <label htmlFor="fichier" className="form-label">
                            Pièce jointe (optionnel - 5 Mo max)
                          </label>
                          <input
                            id="fichier"
                            name="fichier"
                            type="file"
                            onChange={(event) => {
                              setFieldValue(
                                "fichier",
                                event.currentTarget.files[0]
                              );
                            }}
                            className={`form-control ${
                              touched.fichier && errors.fichier
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          {touched.fichier && errors.fichier && (
                            <div className="text-danger mt-1 small d-flex align-items-center">
                              <img
                                src={ErrorIcon}
                                className="me-1"
                                alt="error"
                                width="16"
                              />
                              {errors.fichier}
                            </div>
                          )}
                        </div>

                        {/* Submit Button */}
                        <div className="d-grid">
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="btn-marion"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                          </Button>
                        </div>

                        <p className="text-muted small mt-3 mb-0">
                         Nous tenterons de te répondre dans les plus brefs délais
                        </p>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Info Section */}
      <section className="contact-info-section py-5 bg-light">
        <Container>
          <Row className="text-center">
            <Col md={3} className="mb-4">
              <div className="contact-info-card">
                <i className="fas fa-envelope fa-3x text-primary mb-3"></i>
                <h5>Email</h5>
                <p className="text-muted">
                  <a href="mailto:contact@marion-livraison.com">
                    contact@marion-livraison.com
                  </a>
                </p>
              </div>
            </Col>
            <Col md={3} className="mb-4">
              <div className="contact-info-card">
                <i className="fas fa-phone fa-3x text-primary mb-3"></i>
                <h5>Téléphone</h5>
                <p className="text-muted">
                  {!showPhone ? (
                    <button
                      className="btn btn-link text-primary p-0"
                      onClick={() => setShowPhone(true)}
                    >
                      Afficher le numéro
                    </button>
                  ) : (
                    <a href="tel:0683242067">06 83 24 20 67</a>
                  )}
                </p>
              </div>
            </Col>
            <Col md={3} className="mb-4">
              <div className="contact-info-card">
                <i className="fab fa-whatsapp fa-3x text-success mb-3"></i>
                <h5>WhatsApp</h5>
                <p className="text-muted">
                  <a
                    href="https://wa.me/33683242067"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success btn-sm"
                  >
                    <i className="fab fa-whatsapp me-2"></i>
                    Discuter avec nous
                  </a>
                </p>
              </div>
            </Col>
            <Col md={3} className="mb-4">
              <div className="contact-info-card">
                <i className="fas fa-clock fa-3x text-primary mb-3"></i>
                <h5>Horaires</h5>
                <p className="text-muted">
                  Lundi - Samedi
                  <br />
                  8h - 18h
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
